<script setup lang="ts">
import { ScalarListbox } from '@scalar/components/listbox'
import {
  getHttpMethodInfo,
  REQUEST_METHODS,
} from '@scalar/helpers/http/http-info'
import { objectEntries } from '@scalar/helpers/object/object-entries'
import { cva, cx } from '@scalar/use-hooks/useBindCx'
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    isSquare?: boolean
    method: string
    isEditable?: boolean
  }>(),
  { isSquare: false, isDisable: false, isEditable: false },
)

const emit = defineEmits<{
  (e: 'change', value: string): void
}>()

const method = computed(() => getHttpMethodInfo(props.method))
const methodOptions = computed(() => {
  const options = objectEntries(REQUEST_METHODS).map(([id]) => ({
    id: String(id),
    label: id.toUpperCase(),
    color: getHttpMethodInfo(id).colorClass,
  }))

  // Keep document-defined methods selectable without changing their spelling.
  if (!options.some(({ id }) => id === props.method)) {
    options.push({
      id: props.method,
      label: props.method,
      color: method.value.colorClass,
    })
  }

  return options
})
const selectedMethod = computed({
  get: () => methodOptions.value.find(({ id }) => id === props.method),
  set: (opt) => opt?.id && emit('change', opt.id),
})

const variants = cva({
  base: 'text-center font-code text-3xs justify-center items-center flex',
  variants: {
    isSquare: {
      true: 'px-2.5 whitespace-nowrap font-bold border-r h-fit m-auto',
      false: 'rounded-full',
    },
    isEditable: {
      true: 'http-bg-gradient rounded-md border',
      false: 'cursor-auto',
    },
  },
})

const httpLabel = computed(() => method.value.short)
</script>
<template>
  <!-- Dropdown select -->
  <ScalarListbox
    v-if="isEditable"
    v-model="selectedMethod"
    class="font-code scalar-client mt-1 text-sm"
    :options="methodOptions">
    <div
      class="h-full"
      :class="{ 'pointer-events-none': !isEditable }">
      <button
        class="relative h-full"
        :class="cx(variants({ isSquare, isEditable }), method.colorClass)"
        type="button">
        <span>{{ httpLabel }}</span>
      </button>
    </div>
  </ScalarListbox>
  <!-- Display only -->
  <div
    v-else
    class="relative gap-1 whitespace-nowrap"
    :class="cx(variants({ isSquare, isEditable }), method.colorClass)"
    type="button">
    {{ method.short }}
  </div>
</template>
<style scoped>
.http-bg-gradient {
  background: linear-gradient(rgba(255, 255, 255, 0.75), rgba(0, 0, 0, 0.035));
}
.http-bg-gradient:hover {
  background: linear-gradient(rgba(0, 0, 0, 0.035), rgba(255, 255, 255, 0.75));
}
.dark-mode .http-bg-gradient {
  background: linear-gradient(rgba(255, 255, 255, 0.035), rgba(0, 0, 0, 0.15));
}
.dark-mode .http-bg-gradient:hover {
  background: linear-gradient(rgba(0, 0, 0, 0.15), rgba(255, 255, 255, 0.035));
}
</style>
